import { ListItem as ChakraListItem } from '@chakra-ui/react'
import * as React from 'react'

import { useBgColorCtx } from '~theme/BgColorProvider'
import { TContrastLevel } from '~theme/typings'
import { cn } from '~ui/libs/classnames'
import { getRadiusValue, TRadius } from '~ui/styleGuide/radiuses'
import {
  getBottomSpacingValue,
  getLeftSpacingValue,
  getRightSpacingValue,
  getTopSpacingValue,
  TSpace,
} from '~ui/styleGuide/spaces'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

type TProps = Children & {
  /**
   * unique id
   */
  id?: PropsOf<typeof ChakraListItem>['id']
  /**
   * use to separate the items better
   * if falsy value is passed, no border is rendered
   */
  withBorderTop?: boolean
  /**
   * called on list item click
   * also sets cursor to pointer if anything is passed
   */
  onClick?: () => void
  /**
   * color of the list item,
   * also used to deduce default color of the text
   */
  textContrast?: TContrastLevel
  /**
   * key of radius,
   * or an object with separate key for each corner
   * applied only to the selected item!
   */
  radius?: TRadius
  /**
   * space inside/outside the item,
   * can be single space key,
   * object with `horizontal` and `vertical` attributes
   * or separate values for each side `top`, `right`, `bottom`, `left
   */
  spacing?: TSpace
  /**
   * css class name passed to the html element
   */
  className?: string
  /**
   * boolean flag if the item is selected
   * used to switch between padding (for selected) and margin (for not selected items) as horizontal spacing
   * and also for a border radius (applied only to selected items)
   */
  selected?: boolean
}

export const ListItem: React.FC<TProps> = ({
  id,
  onClick,
  children,
  selected,
  className,
  withBorderTop,
  radius = `none`,
  spacing = `medium`,
  textContrast = `normal`,
}) => {
  const { bgColor, bgColorValue, fgColors, dividerColors } = useBgColorCtx()
  const textColorValue = fgColors[textContrast]

  const topSpacingValue = getTopSpacingValue(spacing)
  const leftSpacingValue = getLeftSpacingValue(spacing)
  const rightSpacingValue = getRightSpacingValue(spacing)
  const bottomSpacingValue = getBottomSpacingValue(spacing)

  // in selected item, use padding for horizontal space (visible bg)
  const paddingValue = selected
    ? `${topSpacingValue} ${rightSpacingValue} ${bottomSpacingValue} ${leftSpacingValue}`
    : `${topSpacingValue} 0 ${bottomSpacingValue} 0`
  const marginValue = selected ? undefined : `0 ${rightSpacingValue} 0 ${leftSpacingValue}`

  // not selected item radius would curve its border
  const borderRadius = selected ? getRadiusValue(radius) : undefined
  const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

  return (
    <ChakraListItem
      {...themeDesignProps}
      id={id}
      // the onPointerDown is used instead of the onClick because of cases,
      // where the list is rendered based od other conditions such as another element (input) focus
      // in such case, pressing the mouse button on the item blurs the input,
      // the list closes, but the click event has not yet been fired, because the mouse button has not been released
      onPointerDown={onClick}
      color={textColorValue}
      margin={marginValue}
      padding={paddingValue}
      className={cn(className)}
      borderRadius={borderRadius}
      cursor={onClick ? `pointer` : `default`}
      backgroundColor={selected ? bgColorValue : undefined}
      borderTop={withBorderTop ? `2px solid ${dividerColors['normal']}` : `none`}
    >
      {children}
    </ChakraListItem>
  )
}
